import { useApiUrl, useDataProvider, useNotification } from "@refinedev/core";
import dayjs from "dayjs";

import { parseUnixTimestamp } from "@/lib/dateUtils";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";

/** An event the picker offers. Same-named events are common, so the date and the venue travel with it. */
export type GroupEventOption = {
  id: string;
  name: string;
  /** The start date on the event's own clock, like "10 Jul 2026". */
  startDate?: string;
  venueName?: string;
};

/** The fields of an `/events/explore` hit's document this search reads. */
type ExploreEventDocument = {
  id?: string;
  name?: string;
  /** Unix seconds. */
  startDate?: number;
  timezone?: string;
  venue?: { name?: string } | null;
};

/** The date as the event's venue shows it. An unknown zone name falls back to the browser's. */
function formatDate(unixSeconds: number, timezone?: string): string {
  let date: dayjs.Dayjs;
  try {
    date = parseUnixTimestamp(unixSeconds, timezone);
  } catch {
    date = dayjs.unix(unixSeconds);
  }
  return date.format("DD MMM YYYY");
}

function toOption(document: ExploreEventDocument): GroupEventOption | null {
  if (!document.id || !document.name) return null;
  return {
    id: document.id,
    name: document.name,
    startDate:
      typeof document.startDate === "number"
        ? formatDate(document.startDate, document.timezone)
        : undefined,
    venueName: document.venue?.name || undefined,
  };
}

/**
 * Finds events to put in a group. It asks `/events/explore` the way `useEventRepository().search`
 * does (name and venue name, 25 hits), but keeps each hit's date and venue, which that one drops.
 */
export const useGroupEventSearch = () => {
  const apiUrl = useApiUrl();
  const { custom } = useDataProvider()();
  const { open } = useNotification();

  async function search(query: string): Promise<GroupEventOption[]> {
    if (!query) return [];
    try {
      const response = await custom?.({
        url: `${apiUrl}/events/explore`,
        method: "get",
        query: {
          q: query,
          queryBy: "venue.name,name",
          limit: 25,
          page: 0,
          filters: query.startsWith('"') ? `name:=${query}` : "",
          sortBy: "_text_match:desc",
          disableCaching: true,
        },
      });
      const hits = (response?.data?.hits ?? []) as {
        document?: ExploreEventDocument;
      }[];
      return hits.flatMap((hit) => {
        const option = hit.document ? toOption(hit.document) : null;
        return option ? [option] : [];
      });
    } catch (error) {
      console.error("Event search failed due to:", error);
      open?.({
        message: `Event search failed due to ${toHumanErrorMessage(error)}`,
        type: "error",
      });
      return [];
    }
  }

  return { search };
};
